import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
  immediate,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import {
  ACTION_TYPES,
  FRESH_PRODUCT_TYPES,
  PLAN_TO_SKU,
  PRODUCT_TYPES,
  SKU_TO_PLAN,
  STATE,
} from './constants.js';
import {
  hasCustomer,
  hasNoCustomer,
  hasActiveCustomer,
  hasCompletePet,
  hasIncompletePetWithName,
  hasPetBirthday,
  hasIncompletePetWithBirthday,
  hasIncompletePetWithBreed,
  hasPetWeight,
  hasIncompletePetWithWeight,
  hasPetActivity,
  hasIncompletePetWithActivity,
  allPetsHaveProductInCart,
  isMixed,
  isFresh,
  hasFreshInCart,
  hasDryInCart,
  isFreshUrl,
  isBakedUrl,
  submitCartFailed,
  prevStateFresh,
  prevStateBaked,
  isCompletePet,
  allPetsComplete,
  selectedDry,
  isAppSession,
  skipAllergiesQuestion,
  showAllergiesQuestion,
  showExtrasPage,
  prevStateAddons,
  showOnboardingInterstitial,
  skipOnboardingInterstitial,
} from './guard';
import { saveCart, goBackReducer } from './functions';

export const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  customer = {},
  pet = {},
  pet_id = '',
  recipes = { fresh: null, dry: null },
  addons = [],
  planType = null,
  stateKey = STATE.initial,
  nextKey = null,
  prevState = {},
  flags = {},
} = {}) => ({
  customer,
  pet,
  pet_id,
  recipes,
  addons,
  planType,
  stateKey,
  nextKey,
  prevState,
  flags,
});

export const onboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.appConfirmation,
        guard(hasActiveCustomer),
        guard(isAppSession),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.appConfirmation,
          };
        }),
      ),
      /**
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.confirmation,
        guard(hasActiveCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.confirmation,
        })),
      ),
      /**
       ** Transitions to Checkout Page if
       ** - hasCustomer: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.checkout,
        guard(hasCustomer),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(
            pet =>
              !!payload.cart.line_items.find(item => item.pet_id === pet.id),
          );
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.checkout,
            nextKey: STATE.confirmation,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - isFreshUrl: true
       ** - hasFreshInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.fresh,
        guard(isFreshUrl),
        guard(hasFreshInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          customer: payload.customer,
          stateKey: STATE.fresh,
          nextKey: STATE.fresh,
          recipes: payload.recipes ?? ctx.recipes,
          planType: payload.planType ?? ctx.planType,
        })),
      ),
      /**
       ** Transitions to Baked Recipe Selection Page if
       ** - isBakedUrl: true
       ** - hasDryInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.baked,
        guard(isBakedUrl),
        guard(hasDryInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          customer: payload.customer,
          stateKey: STATE.baked,
          nextKey: STATE.checkout,
          recipes: payload.recipes ?? ctx.recipes,
          planType: payload.planType ?? ctx.planType,
        })),
      ),
      /**
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.planSelection,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => {
          const pet = payload.pets?.find(
            pet =>
              !payload.cart.line_items.find(item => item.pet_id === pet.id),
          );
          return {
            ...ctx,
            isAppSession: payload.isAppSession || ctx.isAppSession,
            flags: payload.flags,
            customer: payload.customer,
            prevState: { ...ctx },
            stateKey: STATE.planSelection,
            nextKey: STATE.fresh,
            pet,
            pets: payload.pets,
            pet_id: pet?.id ?? '',
            recipes: payload.recipes ?? ctx.recipes,
            planType: payload.planType ?? ctx.planType,
          };
        }),
      ),
      /**
       ** Transitions to User Info Page if
       ** - hasIncompletePetWithActivity: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.userInfo,
        guard(allPetsComplete),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: { ...ctx.flags, ...payload.flags },
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.planSelection,
            customer: payload.customer,
            pet: pet,
          };
        }),
      ),
      /**
       ** Transitions to Allergies Page if
       ** - hasCustomer: true
       ** - hasIncompletePetWithActivity: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.allergies,
        guard(hasIncompletePetWithActivity),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.allergies,
            nextKey: STATE.planSelection,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Activity Selection Page if
       ** - hasIncompletePetWithWeight: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.activity,
        guard(hasIncompletePetWithWeight),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.activity,
            nextKey: STATE.allergies,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Calories Page if
       ** - hasIncompletePetWithBreed: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.calories,
        guard(hasIncompletePetWithBreed),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.calories,
            nextKey: STATE.activity,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Breed Selection Page if
       ** - hasIncompletePetWithBirthday: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.breed,
        guard(hasIncompletePetWithBirthday),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.breed,
            nextKey: STATE.calories,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Birthday Selection Page if
       ** - hasIncompletePetWithName: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.birthday,
        guard(hasIncompletePetWithName),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.birthday,
            nextKey: STATE.breed,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Pup Information Page by default
       **
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupInfo,
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          customer: payload.customer,
        })),
      ),
      /**
       ** Transitions to Recommended Plan Page
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => {
          const pet = payload.pets?.find(
            pet =>
              !payload.cart.line_items.find(item => item.pet_id === pet.id),
          );
          return {
            ...ctx,
            flags: payload.flags,
            customer: payload.customer,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.checkout,
            pet,
            pets: payload.pets,
            pet_id: pet?.id ?? '',
            recipes: payload.recipes ?? ctx.recipes,
            fresh: payload.recipes.fresh ?? ctx.recipes.fresh,
            dry: payload.recipes.dry ?? ctx.recipes.dry,
            planType: payload.planType ?? ctx.planType,
            isAppSession: payload.isAppSession || ctx.isAppSession,
          };
        }),
      ),
    ),
    [STATE.pupInfo]: state(
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Birthday Selection Page if
       ** - hasCustomer: true
       ** - hasPet: true
       */
      transition(
        ACTION_TYPES.toBirthday,
        STATE.birthday,
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.birthday,
          nextKey: STATE.breed,
          pet: payload.pet,
          pet_id: payload.pet.id,
        })),
      ),
    ),
    [STATE.birthday]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Pup Information Page if
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupInfo,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Breed Selection Page if
       ** - hasPetBirthday: true
       */
      transition(
        ACTION_TYPES.toBreed,
        STATE.breed,
        guard(hasPetBirthday),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.breed,
          nextKey: STATE.calories,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.breed]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Birthday Selection Page if
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.birthday,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.birthday,
          nextKey: STATE.breed,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Calories Page if
       ** - hasPetBirthday: true
       */
      transition(
        ACTION_TYPES.toCalories,
        STATE.calories,
        guard(hasPetBirthday),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.calories,
          nextKey: STATE.activity,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.calories]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Breed Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.breed,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.breed,
          nextKey: STATE.calories,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.activity]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Calories Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.calories,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.calories,
          nextKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Allergies Page if
       ** - hasPetActivity: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasPetActivity),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.poop,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.allergies]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Poop Type Selection page if
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toPoop,
        STATE.poop,
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.poop,
          nextKey: STATE.planSelection,
          pet: payload.pet,
          pets: payload.pets,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
          plan: payload,
        })),
      ),
    ),
    [STATE.poop]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Allergies Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.poop,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toPlanSelection,
        STATE.planSelection,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.fresh,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to User Info Step if there is no user created yet and the pet is complete
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toPlanSelection,
        STATE.userInfo,
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.fresh,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
          plan: payload,
        })),
      ),
    ),
    [STATE.userInfo]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Back to Poop Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.poop,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.poop,
          nextKey: STATE.userInfo,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            customer: payload.customer,
            pet: payload.pet,
            pet_id: payload.pet.id,
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - hasCompletePet: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.checkout,
        guard(hasCompletePet),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
          customer: payload.customer,
          pet: payload.pet,
          pet_id: payload.pet.id,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            customer: payload.customer,
            pet: payload.pet,
            pet_id: payload.pet.id,
          };
        }),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.recommendedPlan,
            pet: payload.pet,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Edit Allergies button on No Recipes Found Page
       *
       ** Transitions to Allergies Selection Page
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.poop,
        })),
      ),
      /**
       ** Route: Personalize Plan Button
       *
       ** Transitions to Plan Selection/Meal Prep
       **
       */
      transition(
        ACTION_TYPES.toPlanSelection,
        STATE.planSelection,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.fresh,
          customer: payload.customer ?? ctx.customer,
          pet: payload.pet ?? ctx.pet,
          pets: payload.pets ?? ctx.pets,
          pet_id: payload.pet.id ?? ctx.pet.id,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout
       **
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.recommendedPlan,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.checkout,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
        })),
      ),
    ),
    [STATE.planSelection]: state(
      immediate(
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.checkout,
            customer: ctx.customer,
            pet: ctx.pet ?? payload.pet,
            pet_id: ctx.pet.id ?? payload.pet.id,
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Edit Allergies button on No Recipes Found Page
       *
       ** Transitions to Allergies Selection Page
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.poop,
        })),
      ),

      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.baked,
        guard(selectedDry),
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };
          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            recipes,
          };
        }),
      ),
      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.fresh,
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };
          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            recipes,
          };
        }),
      ),
    ),
    [STATE.fresh]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Direct to Allergies page
       *
       ** Transitions to Allergies Page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
            recipes: { ...ctx.recipes, fresh: payload.recipes },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Baked Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.baked,
        guard(isMixed),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              fresh: payload.recipes || ctx.recipes.fresh,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.baked]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Fresh Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.fresh,
        guard(isMixed),
        reduce(ctx => {
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey: STATE.baked,
            planType: ctx.planType,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button Alternative
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.baked,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       */
      transition(
        ACTION_TYPES.selectedBakedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              dry: payload.recipes || ctx.recipes.dry,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - submitCartFailed: true
       ** - prevStateFresh: true
       */
      transition(
        'done', // this action type is inherent to the invoke function
        STATE.fresh,
        guard(submitCartFailed),
        guard(prevStateFresh),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.fresh,
        })),
      ),
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
        })),
      ),
      /**
       ** Transitions to Baked Recipe Selection Page if
       ** - submitCartFailed: true
       ** - prevStateBaked: true
       */
      transition(
        'done',
        STATE.baked,
        guard(submitCartFailed),
        guard(prevStateBaked),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.baked,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page if
       ** - showReccomendedPlan: false
       */
      transition(
        'done',
        STATE.addons,
        guard(showExtrasPage),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.addons,
          nextKey: STATE.checkout,
        })),
      ),
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.checkout,
          nextKey: STATE.checkout,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       */
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.addons]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Baked Recipe Selection Page if
       ** - prevStateBaked: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.baked,
        guard(prevStateBaked),
        reduce(ctx => {
          let nextKey;

          nextKey =
            ctx.flags.extrasPageCRO !== 'no_page'
              ? STATE.addons
              : STATE.checkout;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button Alternate
       *
       ** Transitions to Fresh Recipe Selection Page if
       ** - prevStateFresh: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.fresh,
        guard(prevStateFresh),
        reduce(ctx => {
          let nextKey;

          nextKey =
            ctx.flags.extrasPageCRO !== 'no_page'
              ? STATE.addons
              : STATE.checkout;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - hasCustomer: true
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.checkout]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Extras Page
       **
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.addons,
        guard(prevStateAddons),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.addons,
            nextKey: STATE.checkout,
          };
        }),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.planSelection,
            nextKey: STATE.checkout,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Edit Pup Button
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toPlanSelection,
        STATE.planSelection,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => {
          const petId = payload.pet_id;
          const { product_type, recipes, portion_size } =
            payload.cart.line_items.find(lineItem => lineItem.pet_id === petId);

          let planType = SKU_TO_PLAN[product_type];

          if (product_type === PLAN_TO_SKU.fresh && portion_size === '0.5') {
            planType = PRODUCT_TYPES.half_fresh;
          }

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.planSelection,
            nextKey: STATE.fresh,
            pet: payload.pets.find(pet => pet.id === payload.pet_id),
            pet_id: payload.pet_id,
            recipes,
            planType,
          };
        }),
      ),
      /**
       ** Route: Add Pup Button
       *
       ** Transitions to Pup Info Page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.toPupInfo,
        STATE.pupInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          pet_id: payload.pet_id,
          customer: payload.customer,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.toConfirmation,
        STATE.appConfirmation,
        guard(hasActiveCustomer),
        guard(isAppSession),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.appConfirmation,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.toConfirmation,
        STATE.confirmation,
        guard(hasActiveCustomer),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.confirmation]: final(),
    [STATE.appConfirmation]: final(),
  },
  createInitialContext,
);

export const shortenedOnboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.appConfirmation,
        guard(hasActiveCustomer),
        guard(isAppSession),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.appConfirmation,
          };
        }),
      ),
      /**
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.confirmation,
        guard(hasActiveCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.confirmation,
        })),
      ),
      /**
       ** Transitions to Checkout Page if
       ** - hasCustomer: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.checkout,
        guard(hasCustomer),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(
            pet =>
              !!payload.cart.line_items.find(item => item.pet_id === pet.id),
          );
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.checkout,
            nextKey: STATE.confirmation,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - isFreshUrl: true
       ** - hasFreshInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.fresh,
        guard(isFreshUrl),
        guard(hasFreshInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          customer: payload.customer,
          stateKey: STATE.fresh,
          nextKey: STATE.fresh,
          recipes: payload.recipes ?? ctx.recipes,
          planType: payload.planType ?? ctx.planType,
        })),
      ),
      /**
       ** Transitions to Baked Recipe Selection Page if
       ** - isBakedUrl: true
       ** - hasDryInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.baked,
        guard(isBakedUrl),
        guard(hasDryInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          customer: payload.customer,
          stateKey: STATE.baked,
          nextKey: STATE.checkout,
          recipes: payload.recipes ?? ctx.recipes,
          planType: payload.planType ?? ctx.planType,
        })),
      ),
      /**
       ** Transitions to Recommended Plan Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       ** - Currently not being used but keeping this code in case we return
       */
      // transition(
      //   ACTION_TYPES.initialLoad,
      //   STATE.planSelection,
      //   guard(hasCustomer),
      //   guard(allPetsComplete),
      //   reduce((ctx, { payload }) => {
      //     const pet = payload.pets?.find(
      //       pet =>
      //         !payload.cart.line_items.find(item => item.pet_id === pet.id),
      //     );
      //     return {
      //       ...ctx,
      //       isAppSession: payload.isAppSession || ctx.isAppSession,
      //       flags: payload.flags,
      //       customer: payload.customer,
      //       prevState: { ...ctx },
      //       stateKey: STATE.planSelection,
      //       nextKey: STATE.fresh,
      //       pet,
      //       pets: payload.pets,
      //       pet_id: pet?.id ?? '',
      //       recipes: payload.recipes ?? ctx.recipes,
      //       planType: payload.planType ?? ctx.planType,
      //     };
      //   }),
      // ),
      /**
       ** Transitions to Recommended Plan Page
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => {
          const pet = payload.pets?.find(
            pet =>
              !payload.cart.line_items.find(item => item.pet_id === pet.id),
          );
          return {
            ...ctx,
            flags: payload.flags,
            customer: payload.customer,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.checkout,
            pet,
            pets: payload.pets,
            pet_id: pet?.id ?? '',
            recipes: payload.recipes ?? ctx.recipes,
            fresh: payload.recipes,
            planType: payload.planType ?? ctx.planType,
            isAppSession: payload.isAppSession || ctx.isAppSession,
          };
        }),
      ),
      /**
       ** Transitions to User Info Page if
       ** - hasIncompletePetWithActivity: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.userInfo,
        guard(allPetsComplete),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: { ...ctx.flags, ...payload.flags },
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.planSelection,
            customer: payload.customer,
            pet: pet,
          };
        }),
      ),
      /**
       ** Transitions to Activity Selection Page if
       ** - hasIncompletePetWithWeight: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.activity,
        guard(hasIncompletePetWithWeight),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.activity,
            nextKey: STATE.allergies,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Calories Page if
       ** - hasIncompletePetWithBreed: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.calories,
        guard(hasIncompletePetWithName),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.calories,
            nextKey: STATE.activity,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),
      /**
       ** Transitions to Allergies Page if
       ** - hasCustomer: true
       ** - hasIncompletePetWithActivity: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.allergies,
        guard(hasIncompletePetWithActivity),
        guard(showAllergiesQuestion),
        reduce((ctx, { payload }) => {
          const pet = payload.pets.find(pet => !isCompletePet(pet));
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.allergies,
            nextKey: STATE.planSelection,
            customer: payload.customer,
            pet: pet,
            pet_id: pet.id,
          };
        }),
      ),

      /**
       ** Transitions to Pup Information Page by default
       **
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupInfo,
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          customer: payload.customer,
        })),
      ),
    ),
    [STATE.pupInfo]: state(
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Calories Page if
       */
      transition(
        ACTION_TYPES.toCalories,
        STATE.calories,
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.calories,
          nextKey: STATE.activity,
          pet: payload.pet,
          pet_id: payload.pet.id,
        })),
      ),
    ),
    [STATE.calories]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to PupInfo Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupInfo,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.calories,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.activity]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Calories Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.calories,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.calories,
          nextKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to User Info Page if
       ** - hasPetActivity: true
       ** - skipAllergiesQuestion: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        guard(skipOnboardingInterstitial),
        guard(skipAllergiesQuestion),
        guard(hasPetActivity),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
      ),
      /**
       * Route: Continue/Next
       * 
       * Transitions to Weight Interstitial if
       - hasPetActivity: true
       - skipAllergiesQuestion: true
       - showOnboardingInterstitial: true
       */
      transition(
        ACTION_TYPES.toWeightInterstitial,
        STATE.weightInterstitial,
        guard(hasPetWeight),
        guard(showOnboardingInterstitial),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.weightInterstitial,
          nextKey: STATE.userInfo,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Allergies Page if
       ** - hasPetActivity: true
       ** - showAllergiesQuestion: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasPetActivity),
        guard(showAllergiesQuestion),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.toUserInfo,
          pet: payload.pet,
        })),
      ),
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.fresh,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.allergies]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to User Info page if
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
          plan: payload,
        })),
      ),
    ),
    [STATE.userInfo]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Back to Activity Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        guard(skipAllergiesQuestion),
        guard(skipOnboardingInterstitial),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.userInfo,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.weightInterstitial,
        guard(showOnboardingInterstitial),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.weightInterstitial,
          nextKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Back to Allergy Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        guard(showAllergiesQuestion),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.userInfo,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          let nextKey;

          nextKey =
            ctx.flags.extrasPageCRO !== 'no_page'
              ? STATE.addons
              : STATE.checkout;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey,
            customer: payload.customer,
            pet: payload.pet,
            pet_id: payload.pet.id,
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - hasCompletePet: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.checkout,
        guard(hasCompletePet),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
          customer: payload.customer,
          pet: payload.pet,
          pet_id: payload.pet.id,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          let nextKey;

          nextKey =
            ctx.flags.extrasPageCRO !== 'no_page'
              ? STATE.addons
              : STATE.checkout;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey,
            customer: payload.customer,
            pet: payload.pet,
            pet_id: payload.pet.id,
          };
        }),
      ),
    ),
    [STATE.weightInterstitial]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Calories Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.weightInterstitial,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to UserInfoPage
       ** - hasPetActivity: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        guard(hasPetActivity),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.recommendedPlan,
          pet: payload.pet,
        })),
      ),
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.fresh,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.recommendedPlan,
            pet: payload.pet,
          };
        }),
        reduce(goBackReducer),
      ),

      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            recipes: { dry: null, fresh: payload.recipes },
          };
        }),
      ),

      transition(
        ACTION_TYPES.selectedMixedRecipes,
        STATE.submitCart,
        guard(isMixed),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            recipes: { dry: payload.recipes.dry, fresh: payload.recipes.fresh },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout
       **
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.recommendedPlan,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.addons,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
        })),
      ),
    ),
    [STATE.planSelection]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Recommended Plan page
       **
       ** Note: goBack action for when user goes to plan selection from full fresh recommended
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
        reduce(goBackReducer),
      ),

      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.baked,
        guard(selectedDry),
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };
          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            recipes,
          };
        }),
      ),
      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.fresh,
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };

          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            recipes,
          };
        }),
      ),
    ),
    [STATE.fresh]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
            recipes: { ...ctx.recipes, fresh: payload.recipes },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Baked Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.baked,
        guard(isMixed),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              fresh: payload.recipes || ctx.recipes.fresh,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.baked]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Fresh Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.fresh,
        guard(isMixed),
        reduce(ctx => {
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey: STATE.baked,
            planType: ctx.planType,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button Alternative
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.baked,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       */
      transition(
        ACTION_TYPES.selectedBakedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              dry: payload.recipes || ctx.recipes.dry,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - submitCartFailed: true
       ** - prevStateFresh: true
       */
      transition(
        'done', // this action type is inherent to the invoke function
        STATE.fresh,
        guard(submitCartFailed),
        guard(prevStateFresh),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.fresh,
        })),
      ),
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
        })),
      ),
      /**
       ** Transitions to Baked Recipe Selection Page if
       ** - submitCartFailed: true
       ** - prevStateBaked: true
       */
      transition(
        'done',
        STATE.baked,
        guard(submitCartFailed),
        guard(prevStateBaked),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.baked,
        })),
      ),
      transition(
        'done',
        STATE.addons,
        guard(showExtrasPage),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.addons,
          nextKey: STATE.checkout,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       */
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.addons]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Baked Recipe Selection Page if
       ** - prevStateBaked: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.baked,
        guard(prevStateBaked),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button Alternate
       *
       ** Transitions to Fresh Recipe Selection Page if
       ** - prevStateFresh: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.fresh,
        guard(prevStateFresh),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),

      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),

      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - hasCustomer: true
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          nextKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.checkout]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Extras Page
       **
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.addons,
        guard(prevStateAddons),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.addons,
            nextKey: STATE.checkout,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Edit Pup Button
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => {
          const petId = payload.pet_id;
          const { product_type, recipes, portion_size } =
            payload.cart.line_items.find(lineItem => lineItem.pet_id === petId);

          let planType = SKU_TO_PLAN[product_type];

          if (product_type === PLAN_TO_SKU.fresh && portion_size === '0.5') {
            planType = PRODUCT_TYPES.half_fresh;
          }

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.fresh,
            pet: payload.pets.find(pet => pet.id === payload.pet_id),
            pet_id: payload.pet_id,
            recipes,
            planType,
          };
        }),
      ),
      /**
       ** Route: Add Pup Button
       *
       ** Transitions to Pup Info Page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.toPupInfo,
        STATE.pupInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          pet_id: payload.pet_id,
          customer: payload.customer,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.toConfirmation,
        STATE.appConfirmation,
        guard(hasActiveCustomer),
        guard(isAppSession),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.appConfirmation,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Confirmation Page if
       ** - hasActiveCustomer: true
       */
      transition(
        ACTION_TYPES.toConfirmation,
        STATE.confirmation,
        guard(hasActiveCustomer),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.confirmation,
        })),
      ),
    ),
    [STATE.confirmation]: final(),
    [STATE.appConfirmation]: final(),
  },
  createInitialContext,
);
